import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { Icon, Button, ArchImage } from '../../components/ui';
import { useAuth } from '../../hooks';
import { trackSignUp } from '../../utils/analytics';

const strengthOf = (pw = '') => {
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return score;
};
const STRENGTH_LABEL = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, loading } = useAuth();

  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [agreed, setAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [registerError, setRegisterError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => { setForm((p) => ({ ...p, [k]: e.target.value })); setErrors((p) => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('Name is required');
    if (!form.email.trim()) e.email = t('Email is required');
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = t('Enter a valid email address');
    if (!form.password) e.password = t('Password is required');
    else if (form.password.length < 8) e.password = t('Password must be at least 8 characters');
    if (!form.confirmPassword) e.confirmPassword = t('Please confirm your password');
    else if (form.password !== form.confirmPassword) e.confirmPassword = t('Passwords do not match');
    if (!agreed) e.agreed = t('Please accept the terms to continue');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setRegisterError('');
    try {
      await register({ name: form.name, email: form.email, password: form.password });
      trackSignUp('email');
      setSuccess(true);
      setTimeout(() => navigate('/'), 2200);
    } catch (err) {
      setRegisterError(err.response?.data?.message || t('Registration failed. Please try again.'));
    }
  };

  const score = strengthOf(form.password);

  return (
    <>
      <SEO title="Create an account" description="Create a free AyodhyaDham.info account to book darshan, puja and stays in Ayodhya." canonicalUrl="/register" noindex />

      <div className="auth">
        <div className="auth__panel">
          <div className="auth__box">
            {success ? (
              <div className="card auth__card text-center">
                <span className="auth__mark" style={{ margin: '0 auto' }}><Icon name="circle-check" size={26} /></span>
                <h1 className="auth__title">{t('Account created!')}</h1>
                <p className="auth__sub">{t('Welcome to AyodhyaDham.info. Taking you home…')}</p>
                <div className="hstack" style={{ justifyContent: 'center', gap: 10, marginTop: 8 }}>
                  <Button to="/">{t('Go to home')}</Button>
                  <Button variant="outline" to="/darshan-booking">{t('Book Darshan')}</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="auth__head">
                  <span className="auth__mark"><Icon name="namaste" size={26} /></span>
                  <h1 className="auth__title">{t('Create your account')}</h1>
                  <p className="auth__sub">{t('Join AyodhyaDham.info — it is free')}</p>
                </div>

                <form className="card auth__card" onSubmit={handleSubmit} noValidate>
                  {registerError && (
                    <div className="notice notice--danger"><Icon name="circle-alert" size={18} /><span>{registerError}</span></div>
                  )}

                  <label className="field">
                    <span className="field__label">{t('Full name')}</span>
                    <span className={`control${errors.name ? ' is-invalid' : ''}`}>
                      <Icon name="user" size={16} />
                      <input value={form.name} onChange={set('name')} placeholder="Ram Das" autoComplete="name" disabled={loading} />
                    </span>
                    {errors.name && <span className="field__error">{errors.name}</span>}
                  </label>

                  <label className="field">
                    <span className="field__label">{t('Email address')}</span>
                    <span className={`control${errors.email ? ' is-invalid' : ''}`}>
                      <Icon name="mail" size={16} />
                      <input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" autoComplete="email" disabled={loading} />
                    </span>
                    {errors.email && <span className="field__error">{errors.email}</span>}
                  </label>

                  <div className="field">
                    <span className="field__label">{t('Password')}</span>
                    <span className={`control${errors.password ? ' is-invalid' : ''}`}>
                      <Icon name="lock" size={16} />
                      <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} placeholder="••••••••" autoComplete="new-password" disabled={loading} />
                      <button type="button" className="pw-toggle" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? t('Hide password') : t('Show password')}>
                        <Icon name={showPassword ? 'eye-off' : 'eye'} size={16} />
                      </button>
                    </span>
                    {form.password && (
                      <>
                        <span className="auth__strength"><span style={{ width: `${(score / 4) * 100}%` }} /></span>
                        <span className="field__hint">{t(STRENGTH_LABEL[score])}</span>
                      </>
                    )}
                    {errors.password && <span className="field__error">{errors.password}</span>}
                  </div>

                  <label className="field">
                    <span className="field__label">{t('Confirm password')}</span>
                    <span className={`control${errors.confirmPassword ? ' is-invalid' : ''}`}>
                      <Icon name="lock" size={16} />
                      <input type={showPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="••••••••" autoComplete="new-password" disabled={loading} />
                    </span>
                    {errors.confirmPassword && <span className="field__error">{errors.confirmPassword}</span>}
                  </label>

                  <div>
                    <label className="check">
                      <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setErrors((p) => ({ ...p, agreed: '' })); }} />
                      <span>
                        {t('I agree to the')} <Link to="/terms">{t('Terms & Conditions')}</Link> {t('and')} <Link to="/privacy">{t('Privacy Policy')}</Link>
                      </span>
                    </label>
                    {errors.agreed && <span className="field__error">{errors.agreed}</span>}
                  </div>

                  <Button type="submit" block disabled={loading} icon={<Icon name="user-plus" size={16} />} iconPosition="left">
                    {loading ? t('Creating account…') : t('Create account')}
                  </Button>
                </form>

                <div className="auth__foot">
                  <Link to="/login">{t('Already have an account?')}</Link>
                  <Link to="/">{t('Back to home')}</Link>
                </div>
              </>
            )}
          </div>
        </div>

        <aside className="auth__art" aria-hidden="true">
          <ArchImage src="/images/design/hero-ram-mandir.webp" alt="" ratio="4/5" />
          <div className="card auth__quote">
            <p className="auth__quote-text">
              <span className="hindi">सियाराम मय सब जग जानी</span>
              “Know the whole world to be filled with Sita and Ram.”
            </p>
            <p className="auth__quote-src">— Ramcharitmanas</p>
          </div>
        </aside>
      </div>
    </>
  );
}
